import { apiClient } from './client';

/**
 * Suivi d'activité V2 (audit trail) — consommation du proxy ADM
 * /api/schools/{code}/v2-activity/* qui relaie les routes /v1/audit/* de la V2.
 */

export interface AuditEntryListItem {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorId: string | null;
  actorLabel: string | null;
  transactionId: string | null;
  changedFieldsCount: number;
  occurredAt: string;
  /** Enrichissement ADM : nom humain de l'objet (« Martin Dupont », « CM2-B »…) */
  entityLabel?: string | null;
  /** Enrichissement ADM : diff avant/après (tronqué à 20 champs) */
  diff?: AuditFieldDiff[];
}

export interface AuditActor {
  id: string | null;
  type: string; // USER | SYSTEM | SCHEDULER | CONNECTOR
  label: string | null;
  role: string | null;
}

export interface AuditContext {
  origin: string; // HTTP | MESSENGER | COMMAND | WEBHOOK
  ipAddress: string | null;
  userAgent: string | null;
  route: string | null;
  requestId: string | null;
  httpMethod: string | null;
  transactionId: string | null;
}

export interface AuditFieldDiff {
  field: string;
  label: string | null;
  type: string; // scalar | enum | relation | date | value_object
  before: unknown;
  after: unknown;
}

export interface AuditEntryDetail {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actor: AuditActor;
  context: AuditContext;
  diff: AuditFieldDiff[];
  snapshotBefore: Record<string, unknown> | null;
  snapshotAfter: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  occurredAt: string;
}

export interface AuditTransaction {
  id: string;
  actor: AuditActor;
  context: AuditContext;
  startedAt: string;
  entries: AuditEntryDetail[];
}

export interface PaginatedAuditEntries {
  items: AuditEntryListItem[];
  total: number;
  page: number;
  limit: number;
  pages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface AuditEntriesParams {
  page?: number;
  limit?: number;
  search?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  /** Id V2 de l'utilisateur (actorId) — activité d'un utilisateur donné */
  userId?: string;
  /** Borne basse ISO-8601 sur la date de l'événement */
  from?: string;
  /** Borne haute ISO-8601 sur la date de l'événement */
  to?: string;
  /** 0 = payload V2 brut sans enrichissement (rapide, pour le premier rendu) */
  enrich?: 0 | 1;
}

export async function getAuditEntries(
  schoolCode: string,
  params: AuditEntriesParams
): Promise<PaginatedAuditEntries> {
  const response = await apiClient.get<PaginatedAuditEntries>(
    `/api/schools/${schoolCode}/v2-activity/entries`,
    { params }
  );
  return response.data;
}

export async function getAuditEntry(
  schoolCode: string,
  entryId: string
): Promise<AuditEntryDetail> {
  const response = await apiClient.get<AuditEntryDetail>(
    `/api/schools/${schoolCode}/v2-activity/entries/${entryId}`
  );
  return response.data;
}

export async function getAuditTransaction(
  schoolCode: string,
  transactionId: string
): Promise<AuditTransaction> {
  const response = await apiClient.get<AuditTransaction>(
    `/api/schools/${schoolCode}/v2-activity/transactions/${transactionId}`
  );
  return response.data;
}
